import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import '../../../css/accounts/reports/SalesReport.css';

const SalesReport = () => {
  const [salesRecords, setSalesRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Date Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination States (15 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 🎯 Fetch Paid Sales Records from Firestore 'orderAcceptedbyAgent' Collection
  useEffect(() => {
    const q = query(collection(db, 'orderAcceptedbyAgent'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs
        .map((doc) => {
          const data = doc.data();
          const payment = data.payment || {};

          return {
            id: doc.id,
            createdAt: data.createdAt ? data.createdAt.toDate() : null,
            displayOrderId: data.displayOrderId || data.autoGeneratedId || doc.id,
            customerName: data.customerName || data.userName || 'N/A',
            customerPhone: data.customerPhone || data.userPhone || 'N/A',
            totalPayable: Number(payment.totalPayable) || 0,
            paymentStatus: payment.status || 'Unpaid',
          };
        })
        // Filter ONLY records where payment status is 'paid'
        .filter((item) => item.paymentStatus.toLowerCase() === 'paid');

      setSalesRecords(records);
      setFilteredRecords(records);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching agent accepted sales report: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 🎯 Filter Logic (Search & Date Range)
  useEffect(() => {
    let result = salesRecords;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.displayOrderId.toLowerCase().includes(term) ||
          item.customerName.toLowerCase().includes(term) ||
          item.customerPhone.includes(term)
      );
    }

    if (startDate) {
      result = result.filter((item) => {
        if (!item.createdAt) return false;
        const itemDate = item.createdAt.toISOString().split('T')[0];
        return itemDate >= startDate;
      });
    }

    if (endDate) {
      result = result.filter((item) => {
        if (!item.createdAt) return false;
        const itemDate = item.createdAt.toISOString().split('T')[0];
        return itemDate <= endDate;
      });
    }

    setFilteredRecords(result);
    setCurrentPage(1); // Reset pagination on filter
  }, [searchTerm, startDate, endDate, salesRecords]);

  // Aggregate Calculation
  const totalOrdersCount = filteredRecords.length;
  const totalSalesAmount = filteredRecords.reduce((sum, item) => sum + item.totalPayable, 0);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayedRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="sales-report-container">
      <div className="sales-report-card">
        
        {/* Header Title */}
        <div className="report-header">
          <div>
            <h2 className="report-title">Sales Report</h2>
            <p className="report-subtitle">Paid order transactions completed via accepted agent orders</p>
          </div>
          <button className="btn-export-csv" onClick={() => window.print()}>
            🖨️ Print / Export
          </button>
        </div>

        {/* Top Summary Cards */}
        <div className="report-summary-grid">
          <div className="summary-card total-orders-card">
            <div className="card-info">
              <span className="card-label">Total Paid Orders</span>
              <span className="card-value">{totalOrdersCount}</span>
            </div>
            <div className="card-icon">📦</div>
          </div>

          <div className="summary-card total-revenue-card">
            <div className="card-info">
              <span className="card-label">Total Sales Amount</span>
              <span className="card-value">AED {totalSalesAmount.toFixed(2)}</span>
            </div>
            <div className="card-icon">💰</div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="report-filter-bar">
          <div className="filter-group main-search">
            <input
              type="text"
              placeholder="Search Order ID, Customer Name, Phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="report-input"
            />
          </div>

          <div className="filter-group date-filters">
            <div className="date-field">
              <label>From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="report-input date-input"
              />
            </div>
            <div className="date-field">
              <label>To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="report-input date-input"
              />
            </div>
          </div>
        </div>

        {/* Sales Table */}
        <div className="report-table-wrapper">
          <table className="report-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>S.No</th>
                <th>Date</th>
                <th>Order ID</th>
                <th>Customer Name</th>
                <th>Phone Number</th>
                <th style={{ textAlign: 'center' }}>Payment Status</th>
                <th style={{ textAlign: 'right' }}>Amount (AED)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="status-cell">Loading sales report...</td>
                </tr>
              ) : currentDisplayedRecords.length === 0 ? (
                <tr>
                  <td colSpan="7" className="status-cell">No paid sales records found.</td>
                </tr>
              ) : (
                currentDisplayedRecords.map((item, index) => {
                  const serialNumber = indexOfFirstItem + index + 1;
                  return (
                    <tr key={item.id}>
                      <td><strong>{serialNumber}</strong></td>
                      <td>
                        {item.createdAt
                          ? item.createdAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                          : 'N/A'
                        }
                      </td>
                      <td className="order-id-code">{item.displayOrderId}</td>
                      <td className="customer-name-text">{item.customerName}</td>
                      <td>{item.customerPhone}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="payment-pill paid">
                          {item.paymentStatus}
                        </span>
                      </td>
                      <td className="currency-cell" style={{ textAlign: 'right' }}>
                        AED {item.totalPayable.toFixed(2)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {!loading && filteredRecords.length > 0 && (
          <div className="report-pagination-bar">
            <div className="pagination-info">
              Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredRecords.length)}</strong> of <strong>{filteredRecords.length}</strong> records
            </div>

            <div className="pagination-buttons">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn nav-btn"
              >
                ◀ Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, idx, arr) => {
                  const prev = arr[idx - 1];
                  return (
                    <React.Fragment key={page}>
                      {prev && page - prev > 1 && <span className="pagination-dots">...</span>}
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`page-btn ${currentPage === page ? 'active-page' : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn nav-btn"
              >
                Next ▶
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SalesReport;